import React from 'react';
import {
  find,
  get,
  isEmpty,
  isFinite,
  isNil,
} from 'lodash';
import { PLAZA_TEMPLATE_TYPES } from '../../../helper';
import HardConstraintIcon from '../../../resources/Constraint_Node_info_icon.svg';
import MeasurementPointIcon from '../../../resources/Meter_Node_info_icon.svg';
import {
  CHARGER_NODE_TYPE,
  ChargerNodeConfig,
  CHARGING_POLICY_NODE_TYPE,
  chargingPolicyMap,
  ChargingPolicyNodeConfig,
  ConstrainsNodeConfig,
  CONSTRAINT_NODE_TYPE,
  CONSTRAINT_NODE_TYPES,
  DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE,
  JUNCTION_NODE_TYPE,
  JunctionNodeConfig,
  LOAD_NODE_TYPE,
  LOAD_SOFT_CONSTRAINT_NODE_TYPE,
  LoadNodeConfig,
  LoadSoftConstraintNodeConfig,
  MEASUREMENT_POINT_NODE_TYPE,
  MeasurementPointNodeConfig,
  NODES_WITHOUT_SOCKET_IN,
  POWER_SOFT_CONSTRAINT_NODE_TYPE,
  PowerSoftConstraintNodeConfig,
  RENEWABLE_NODE_TYPE,
  RENEWABLE_WITH_STORAGE_NODE_TYPE,
  RenewableNodeConfig,
  RenewableStorageNodeConfig,
  RESOURCE_NODE_TYPES,
  ROOT_NODE_TYPE,
  RootNodeConfig,
  STATE_TYPE_CHARGING,
  STATE_TYPE_CHARGING_WITH_ERROR,
  STATE_TYPE_CLASS_NAMES,
  STATE_TYPE_DISCHARGING,
  STATE_TYPE_DISCHARGING_WITH_ERROR,
  STATE_TYPE_EV_CONNECTED,
  STATE_TYPE_OFFLINE,
  STORAGE_NODE_TYPE,
  StorageNodeConfig,
  undefinedNodeParamValue,
} from '../../constants';
import { detectDemoMode } from '../../Demo/helper';
import ChargerNode from '../Node/BlockNode/ChargerNode';
import ChargingPolicyNode from '../Node/BlockNode/ChargingPolicyNode';
import ConstraintNode from '../Node/BlockNode/ConstraintNode';
import JunctionNode from '../Node/BlockNode/JunctionNode';
import LoadNode from '../Node/BlockNode/LoadNode';
import LoadSoftConstraintNode from '../Node/BlockNode/LoadSoftConstraintNode';
import MeasurementPointNode from '../Node/BlockNode/MeasurementPointNode';
import PowerSoftConstraintNode from '../Node/BlockNode/PowerSoftConstraintNode';
import RenewableNode from '../Node/BlockNode/RenewableNode';
import RenewableStorageNode from '../Node/BlockNode/RenewableStorageNode';
import RootNode from '../Node/BlockNode/RootNode';
import StorageNode from '../Node/BlockNode/StorageNode';

import { COLUMN_WIDTH } from './hierarchy';

export const isResourceNodeType = (nodeType) => RESOURCE_NODE_TYPES.indexOf(nodeType) > -1;
export const isConstraintNodeType = (nodeType) => CONSTRAINT_NODE_TYPES.indexOf(nodeType) > -1;

const NODE_PARAM_HEIGHT = 13;
const NODE_PARAM_MARGIN = 5;

const getFractionDigitsByUnit = (unit) => {
  switch (unit) {
    case 'kWh':
      return 3;
    default:
      return 2;
  }
};

export const renderValue = (value, unit = '', multiply = 1, defaultResult = undefinedNodeParamValue) => {
  if (!value && value !== 0) {
    return defaultResult;
  }

  if (!isNaN(value)) {
    const multiplyValue = value * multiply;

    return `${multiplyValue.toFixed(getFractionDigitsByUnit(unit))}${unit}`;
  }

  return `${value}${unit}`;
};

export const makeParams = (configParams) => (node) => configParams.map((param) => ({
  label: param.label,
  value: renderValue(node[param.name], param.unit, param.multiply),
  valuable: !!param.valuable,
}));

export const NODES = [
  {
    type: ROOT_NODE_TYPE,
    component: RootNode,
    config: RootNodeConfig,
  },
  {
    type: CHARGER_NODE_TYPE,
    component: ChargerNode,
    config: ChargerNodeConfig,
  },
  {
    type: CONSTRAINT_NODE_TYPE,
    component: ConstraintNode,
    config: ConstrainsNodeConfig,
  },
  {
    type: POWER_SOFT_CONSTRAINT_NODE_TYPE,
    component: PowerSoftConstraintNode,
    config: PowerSoftConstraintNodeConfig,
  },
  {
    type: LOAD_SOFT_CONSTRAINT_NODE_TYPE,
    component: LoadSoftConstraintNode,
    config: LoadSoftConstraintNodeConfig,
  },
  {
    type: MEASUREMENT_POINT_NODE_TYPE,
    component: MeasurementPointNode,
    config: MeasurementPointNodeConfig,
  },
  {
    type: LOAD_NODE_TYPE,
    component: LoadNode,
    config: LoadNodeConfig,
  },
  {
    type: JUNCTION_NODE_TYPE,
    component: JunctionNode,
    config: JunctionNodeConfig,
  },
  {
    type: RENEWABLE_NODE_TYPE,
    component: RenewableNode,
    config: RenewableNodeConfig,
  },
  {
    type: STORAGE_NODE_TYPE,
    component: StorageNode,
    config: StorageNodeConfig,
  },
  {
    type: RENEWABLE_WITH_STORAGE_NODE_TYPE,
    component: RenewableStorageNode,
    config: RenewableStorageNodeConfig,
  },
  {
    type: CHARGING_POLICY_NODE_TYPE,
    component: ChargingPolicyNode,
    config: ChargingPolicyNodeConfig,
  },
  {
    type: DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE,
    component: LoadSoftConstraintNode,
    config: LoadSoftConstraintNodeConfig,
  },
];

export const getNodeComponentByType = (nodeType) => {
  const foundNode = NODES.find((node) => node.type === nodeType);

  if (foundNode) {
    return foundNode.component;
  }
  throw new Error(`Node with type "${nodeType}" wasn't found`);
};

export const getNodeConfigByType = (nodeType) => {
  const foundNode = NODES.find((node) => node.type === nodeType);

  if (!foundNode) {
    throw new Error(`Node with type "${nodeType}" wasn't found`);
  }

  return foundNode.config;
};

export const isDefinedNodeParam = (param) => !param.valuable || param.value !== undefinedNodeParamValue;

export const calculateDynamicParamsHeight = (dynamicParams) =>
  !isEmpty(dynamicParams)
    ? dynamicParams.filter(isDefinedNodeParam).length * (NODE_PARAM_HEIGHT + NODE_PARAM_MARGIN) + 5
    : 0;

export const calculateNodeHeightByParams = (infoMarkers, staticParams, dynamicParams) => {
  let height = 40;

  height += infoMarkers ? 30 : 0;
  height += !isEmpty(staticParams) ? staticParams.filter(isDefinedNodeParam).length * (NODE_PARAM_HEIGHT + NODE_PARAM_MARGIN) : 0;
  height += calculateDynamicParamsHeight(dynamicParams);

  return height;
};

export const calculateNodeHeight = (props) => {
  const { infoMarkers, topologyNodeType } = props;
  const nodeConfig = NODES.find((node) => node.type === topologyNodeType).config(props);
  const staticParams = makeParams(nodeConfig.staticParams || [])(props);
  const dynamicParams = makeParams(nodeConfig.dynamicParams || [])(props);

  return calculateNodeHeightByParams(infoMarkers, staticParams, dynamicParams);
};

export const calculateNodesSize = (nodes) => nodes.reduce((acc, node) => {
  const config = getNodeConfigByType(node.topologyNodeType)(node);

  acc.push({
    ...node,
    ...config,
    width: config.width || COLUMN_WIDTH,
    height: config.height || calculateNodeHeight(node),
  });

  return acc;
}, []);

export const extractStaticNodeData = (nodeData, nodes) => {
  const dynamicMaxLoad = get(nodeData, 'dynamicSection.maxLoad', null);
  const staticMaxLoad = get(nodeData, 'staticSection.maxCurrentLimit');
  const monitorMaxLoadSc = dynamicMaxLoad === null ? staticMaxLoad : dynamicMaxLoad;
  const demoMode = detectDemoMode();

  return {
    nodes,
    id: get(nodeData, 'id'),
    chargerUuid: get(nodeData, 'staticSection.chargerUuid'),
    title: get(nodeData, 'staticSection.nodeName'),
    withHardConstraint: get(nodeData, 'staticSection.withHardConstraint'),
    withMeasurementPoint: get(nodeData, 'staticSection.withMeasurementPoint'),
    nominalVoltage: get(nodeData, 'staticSection.voltage'),
    offlineCurrentLimit: get(nodeData, 'staticSection.offlineCurrentLimit'),
    defaultPower: get(nodeData, 'staticSection.defaultPower'),
    maxLoad: get(nodeData, 'staticSection.maxCurrentLimit'),
    minLoad: get(nodeData, 'staticSection.minCurrentLimit'),
    bidirectional: get(nodeData, 'staticSection.discharging'),
    maxCapacity: get(nodeData, 'staticSection.maxStorageCapacity'),
    offlineLoad: get(nodeData, 'staticSection.defaultOfflineCurrentLimit'),
    maxPower: window.location.pathname.includes('editor')
      ? get(nodeData, 'staticSection.maxPower')
      : demoMode === 'demo'
        ? get(nodeData, 'staticSection.maxPower')
        : get(nodeData, 'dynamicSection.maxPower'),
    condition: get(nodeData, 'staticSection.condition'),
    phases: get(nodeData, 'staticSection.phases'),
    junctionNodeId: get(nodeData, 'staticSection.junctionNodeId'),
    positionX: get(nodeData, 'staticSection.positionX'),
    positionY: get(nodeData, 'staticSection.positionY'),
    minLoadLimit: get(nodeData, 'staticSection.minLoadLimit'),
    maxLoadSC: window.location.pathname.includes('editor')
      ? staticMaxLoad
      : monitorMaxLoadSc,
    adjustedMaxLoad: get(nodeData, 'staticSection.adjustedMaxLoad'),
    measurementNodeType: get(nodeData, 'staticSection.measurementNodeType'),
  };
};

const loadPhases = {
  1: 'presentLoadPhA',
  2: 'presentLoadPhB',
  3: 'presentLoadPhC',
};
const powerPhases = {
  1: 'presentPowerPhA',
  2: 'presentPowerPhB',
  3: 'presentPowerPhC',
};
const rotation = ['1', '2', '3'];

const summarizeChargersPhases = (childNodes, dynamicData, parentNode, templateType) => {
  const isALMPlaza = PLAZA_TEMPLATE_TYPES.AdaptiveLoadManagement === templateType;

  for (const childNode of childNodes) {
    const childDynamicData = get(dynamicData, childNode.id, {});
    const childDynamicSection = get(childNode, 'dynamicSection', {});
    const childLoadPhases = rotation.map((item) => loadPhases[item]);
    const childPowerPhases = rotation.map((item) => powerPhases[item]);

    for (const phaseNumber of rotation) {
      const parentPhaseNumber = rotation.indexOf(phaseNumber) + 1;
      const parentLoadPhase = loadPhases[parentPhaseNumber];
      const parentPowerPhase = powerPhases[parentPhaseNumber];

      const childLoadPhase = childLoadPhases[rotation.indexOf(phaseNumber)];
      const childPowerPhase = childPowerPhases[rotation.indexOf(phaseNumber)];

      if (childDynamicData) {
        const condition = isALMPlaza
          ? childNode.topologyNodeType !== CHARGER_NODE_TYPE
          : childNode.topologyNodeType !== CHARGER_NODE_TYPE && parentNode.topologyNodeType !== MEASUREMENT_POINT_NODE_TYPE;

        const isMeasurement = childNode.topologyNodeType === MEASUREMENT_POINT_NODE_TYPE;

        if (condition) {
          const childLoadValue = childDynamicData[childLoadPhase];
          const childPowerValue = childDynamicData[childPowerPhase];

          if (isALMPlaza) {
            if (isMeasurement) {
              const childLoadValueMN = get(childDynamicSection, childLoadPhase, null);
              const childPowerValueMN = get(childDynamicSection, childPowerPhase, null);

              if (!isNil(childLoadValueMN) && !isNil(dynamicData[childNode.id])) {
                dynamicData[childNode.id][childLoadPhase] = childLoadValueMN;
                dynamicData[parentNode.id][parentLoadPhase] += childLoadValueMN;
              }
              if (!isNil(childPowerValueMN) && !isNil(dynamicData[childNode.id])) {
                dynamicData[childNode.id][childPowerPhase] = childPowerValueMN;
                dynamicData[parentNode.id][parentPowerPhase] += childPowerValueMN;
              }
            } else {
              if (parentNode.topologyNodeType !== MEASUREMENT_POINT_NODE_TYPE) {
                if (!isNil(childLoadValue)) {
                  dynamicData[parentNode.id][parentLoadPhase] += childLoadValue;
                }
                if (!isNil(childPowerValue)) {
                  dynamicData[parentNode.id][parentPowerPhase] += childPowerValue;
                }
              }
            }
          } else {
            if (!isNil(childLoadValue)) {
              dynamicData[parentNode.id][parentLoadPhase] += childLoadValue;
            }
            if (!isNil(childPowerValue)) {
              dynamicData[parentNode.id][parentPowerPhase] += childPowerValue;
            }
          }
        } else {
          const childLoadValue = get(childDynamicSection, childLoadPhase);
          const childPowerValue = get(childDynamicSection, childPowerPhase);

          const isOnline = get(childDynamicSection, 'online');
          const isMeasurement = parentNode.topologyNodeType === MEASUREMENT_POINT_NODE_TYPE;

          if (isALMPlaza && isMeasurement) {
            const parentLoadValue = get(parentNode, `dynamicSection.${parentLoadPhase}`, null);
            const parentPowerValue = get(parentNode, `dynamicSection.${parentPowerPhase}`, null);

            dynamicData[parentNode.id][parentLoadPhase] = parentLoadValue;
            dynamicData[parentNode.id][parentPowerPhase] = parentPowerValue;
          } else if (isOnline) {
            if (!isNil(childLoadValue)) {
              dynamicData[parentNode.id][parentLoadPhase] += childLoadValue;
            }
            if (!isNil(childPowerValue)) {
              dynamicData[parentNode.id][parentPowerPhase] += childPowerValue;
            }
          }
        }
      }
    }
  }
};

const getNodesFromParent = (nodes, node) => nodes.filter((item) => find(node.children, { id: item.id }));
const getNodeParent = (nodes, node) => nodes.find((item) => find(item.children, { id: node.id }));

const metrics = [
  'presentLoadPhA',
  'presentLoadPhB',
  'presentLoadPhC',
  'presentPowerPhA',
  'presentPowerPhB',
  'presentPowerPhC',
  'presentVoltagePhA',
  'presentVoltagePhB',
  'presentVoltagePhC',
  'sessionEnergy',
];

const setDefaultPhases = (dynamicData, node, templateType) => {
  const isALMPlaza = PLAZA_TEMPLATE_TYPES.AdaptiveLoadManagement === templateType;

  const resetMetrics = metrics.reduce((obj, metric) => {
    obj[metric] = null;

    return obj;
  }, {});

  if (isALMPlaza && node.topologyNodeType === MEASUREMENT_POINT_NODE_TYPE) {
    return dynamicData[node.id];
  }

  return {
    ...dynamicData[node.id],
    ...resetMetrics,
  };
};

const getParentRecursive = (dynamicData, nodes = [], childNodes = [], templateType) => {
  for (const node of childNodes) {
    const parent = getNodeParent(nodes, node);

    if (!parent) {
      return;
    }

    dynamicData[parent.id] = setDefaultPhases(dynamicData, parent, templateType);
    const children = getNodesFromParent(nodes, parent);

    summarizeChargersPhases(children, dynamicData, parent, templateType);

    getParentRecursive(dynamicData, nodes, [parent], templateType);
  }
};

export const extractDynamicNodeData = (nodeData) => ({
  // presentLoad: get(nodeData, 'presentLoad'),
  presentLoadPhA: get(nodeData, 'presentLoadPhA', null),
  presentLoadPhB: get(nodeData, 'presentLoadPhB', null),
  presentLoadPhC: get(nodeData, 'presentLoadPhC', null),
  presentPowerPhA: get(nodeData, 'presentPowerPhA', null),
  presentPowerPhB: get(nodeData, 'presentPowerPhB', null),
  presentPowerPhC: get(nodeData, 'presentPowerPhC', null),
  presentVoltagePhA: get(nodeData, 'presentVoltagePhA', null),
  presentVoltagePhB: get(nodeData, 'presentVoltagePhB', null),
  presentVoltagePhC: get(nodeData, 'presentVoltagePhC', null),
  // presentPower: get(nodeData, 'power'),
  requestedLoad: get(nodeData, 'chargingLoadRequested'),
  requestedDischargingLoad: get(nodeData, 'dischargingLoadRequested'),
  loadLimit: get(nodeData, 'recommendedChargingLoadLimit'),
  dischargingLoadLimit: get(nodeData, 'dischargingLoadLimit'),
  chargingPowerRequested: get(nodeData, 'chargingPowerRequested'),
  dischargingPowerRequested: get(nodeData, 'dischargingPowerRequested'),
  powerLimit: get(nodeData, 'powerLimit') || get(nodeData, 'chargingPowerLimit'),
  chargingPowerLimit: get(nodeData, 'maxPower'),
  dischargingPowerLimit: get(nodeData, 'dischargingPowerLimit'),
  sessionType: get(nodeData, 'sessionType'),
  sessionEnergy: get(nodeData, 'sessionEnergy'),
  presentSoc: get(nodeData, 'presentSoc'),
  // presentVoltage: get(nodeData, 'presentVoltage'),
  presentCapacity: get(nodeData, 'presentCapacity'),
  reason: get(nodeData, 'reason'),
  online: get(nodeData, 'online'),
  evConnected: get(nodeData, 'evConnected'),
  energyMode: get(nodeData, 'energyMode'),
  energyDirection: get(nodeData, 'energyDirection'),
  chargingError: get(nodeData, 'chargingError'),
  authorizationRequested: get(nodeData, 'authorizationRequested'),
  applyingLimit: get(nodeData, 'applyingLimit'),
  chargingPolicy: get(chargingPolicyMap[get(nodeData, 'chargingPolicy')], 'label'),
  pgePeriod: get(nodeData, 'pgePeriod'),
  confirmedChargingLoadLimit: get(nodeData, 'confirmedChargingLoadLimit'),
});

export const normalizeNodeData = (nodes, { dynamicData = {}, deleteStatic = true, templateType }) => {
  const phaseNodes = nodes.filter((node) => node.topologyNodeType === CHARGER_NODE_TYPE || node.topologyNodeType === MEASUREMENT_POINT_NODE_TYPE);

  getParentRecursive(dynamicData, nodes, phaseNodes, templateType);

  return nodes.map((node) => {
    const staticParams = extractStaticNodeData(node, nodes);
    const dynamicParams = extractDynamicNodeData(dynamicData[node.id]);

    const normalizedNode = {
      ...node,
      ...staticParams,
      ...dynamicParams,
    };

    if (deleteStatic) {
      delete normalizedNode.staticSection;
    }

    return normalizedNode;
  });
};

export const buildSourceInfoMarkers = ({ withHardConstraint, withMeasurementPoint }) => {
  let infoMarkers;

  if (withHardConstraint || withMeasurementPoint) {
    infoMarkers = (
      <>
        {
          withHardConstraint
          && (
            <image
              x={5}
              y={5}
              height={20}
              href={HardConstraintIcon}
            />
          )
        }
        {
          withMeasurementPoint
          && (
            <image
              x={30}
              y={5}
              height={20}
              href={MeasurementPointIcon}
            />
          )
        }
      </>
    );
  }

  return infoMarkers;
};

export const sliceString = (string, maxLength) => (string.length <= maxLength ? string : `${string.slice(0, maxLength)}...`);

export const isExternalSourceNode = (node) => [RENEWABLE_NODE_TYPE, STORAGE_NODE_TYPE, RENEWABLE_WITH_STORAGE_NODE_TYPE]
  .includes(node.topologyNodeType);

export const getCurrentStateClassName = (props) => {
  let className = '';

  if (props.evConnected) {
    className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_EV_CONNECTED];
  }

  if (!props.online) {
    className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_OFFLINE];
  }

  if (props.energyDirection === 'IN') {
    if (props.chargingError) {
      className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_CHARGING_WITH_ERROR];
    } else {
      className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_CHARGING];
    }
  }

  if (props.energyDirection === 'OUT') {
    if (props.chargingError) {
      className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_DISCHARGING_WITH_ERROR];
    } else {
      className = STATE_TYPE_CLASS_NAMES[STATE_TYPE_DISCHARGING];
    }
  }

  return className;
};

export const putPropsToEventHandler = (handler, props) => (event) => (handler ? handler(event, props) : () => {
});

export const putPropsToEventHandlers = (handlers, props) => Object.keys(handlers).reduce((handlersWithProps, key) => {
  handlersWithProps[key] = putPropsToEventHandler(handlers[key], props);

  return handlersWithProps;
}, {});

export const shouldShowSocketIn = (sourceNode, targetNode, targetIsConnected) => (
  (targetNode.topologyNodeType === ROOT_NODE_TYPE || targetIsConnected)
    && (
      (
        sourceNode
        && sourceNode.id !== targetNode.id
        && !NODES_WITHOUT_SOCKET_IN.includes(targetNode.topologyNodeType)
        && !isExternalSourceNode(sourceNode)
      ) || (
        sourceNode
        && sourceNode.id !== targetNode.id
        && !NODES_WITHOUT_SOCKET_IN.includes(targetNode.topologyNodeType)
        && targetNode.topologyNodeType === JUNCTION_NODE_TYPE
      )
    )
);

export const areNodePositionsFixed = (nodes) => {
  if (!nodes || !nodes.length) return false;

  let isFixed = true;

  nodes.forEach(
    (node) => {
      if (
        !isFinite(node.staticSection.positionX)
        || !isFinite(node.staticSection.positionY)
      ) isFixed = false;
    },
  );

  return isFixed;
};

export const getZoomCof = (props) => {
  const {
    schemaWidth, schemaHeight, drawingAreaWidth, drawingAreaHeight,
  } = props;

  return {
    x: 1 + (schemaWidth - drawingAreaWidth) / drawingAreaWidth,
    y: 1 + (schemaHeight - drawingAreaHeight) / drawingAreaHeight,
  };
};

export const calculateNodeChangedPosition = ({
  node, start, schemaParams, eventX, eventY,
}) => {
  const { positionX, positionY } = node;
  const { client } = start;

  const diff = {
    x: client.x - eventX,
    y: client.y - eventY,
  };

  const cof = getZoomCof(schemaParams);
  const delta = Math.max(...Object.values(cof));

  let newPositionX = positionX - (diff.x * delta);

  if (newPositionX < 1) {
    newPositionX = 1;
  }

  return {
    positionX: newPositionX,
    positionY: positionY - (diff.y * delta),
  };
};

export const splitResourceNodes = (nodes) => {
  const regular = [];
  const resources = [];

  nodes.forEach(
    (node) => {
      if (RESOURCE_NODE_TYPES.includes(node.topologyNodeType)) {
        resources.push(node);
      } else {
        regular.push(node);
      }
    },
  );

  return [resources, regular];
};
